import {
  CheckInCard,
  CheckInCardProps
} from '@gettactic/components/src/components/reservation-card/CheckInCard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/api';
import { useAuthenticated } from '@gettactic/hooks';
import { toast } from 'react-hot-toast';
import { useState } from 'react';

export type CheckInCardOnWebProps = Omit<
  CheckInCardProps,
  'onCheckin' | 'onCheckout' | 'loading' | 'showCheckInButton'
>;

export const CheckInCardOnWeb = ({
  reservation,
  onEdit,
  onCancel,
  ...restProps
}: CheckInCardOnWebProps) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  // If the remote_checkin setting is false, we need to hide the check-in / check-out buttons
  // TODO: We will need to decide what to do with this logic as we add additional resource types
  const office = authenticatedUser?.offices?.byId[currentOffice.id];
  const officeHasRoomEnableCheckIn = office?.meeting_room_remote_checkin;
  const officeHasWorkspaceEnableCheckIn = office?.workspace_remote_checkin;

  // Determine if we should show the check-in button based on office settings
  // Logic may vary depending on resource type
  const showCheckInButton =
    officeHasRoomEnableCheckIn || officeHasWorkspaceEnableCheckIn;

  const checkinMutation = useMutation(
    async ({
      resourceId,
      scheduleId,
      slotId
    }: {
      resourceId: string;
      scheduleId: string;
      slotId: string;
    }) => {
      setLoading(true);
      await api.client.schedules.checkin(resourceId, scheduleId, slotId);

      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['myAgenda'] });
    },
    {
      onSuccess: () => {
        setLoading(false);
        toast.success("You're checked in");
      },
      onError: () => {
        setLoading(false);
        toast.error(`Sorry we couldn't complete the check in`);
      }
    }
  );

  const checkoutMutation = useMutation(
    async ({
      resourceId,
      scheduleId,
      slotId
    }: {
      resourceId: string;
      scheduleId: string;
      slotId: string;
    }) => {
      setLoading(true);
      await api.client.schedules.checkout(resourceId, scheduleId, slotId);
      // Use proper query invalidation format for tanstack/react-query
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['myAgenda'] });
      queryClient.invalidateQueries(['tableOverview']);
    },
    {
      onSuccess: () => {
        setLoading(false);
        toast.success("You've checked out");
      },
      onError: () => {
        setLoading(false);
        toast.error(`Sorry we couldn't complete the checkout`);
      }
    }
  );

  const handleCheckin = (
    resourceId: string,
    scheduleId: string,
    slotId: string
  ) => {
    checkinMutation.mutate({ resourceId, scheduleId, slotId });
  };

  const handleCheckout = (
    resourceId: string,
    scheduleId: string,
    slotId: string
  ) => {
    checkoutMutation.mutate({ resourceId, scheduleId, slotId });
  };

  return (
    <CheckInCard
      reservation={reservation}
      onCheckin={handleCheckin}
      onCheckout={handleCheckout}
      loading={loading}
      showCheckInButton={showCheckInButton}
      onEdit={onEdit}
      onCancel={onCancel}
      {...restProps}
    />
  );
};
